import { useEffect, useMemo, useRef, useState } from 'react';
import { INTROS, boardLine, ordinal, pick } from '@/lib/kudos';
import { useGame, boardAt } from '@/store/game';
import { sfx } from '@/audio/sfx';
import { AppHeader } from '@/components/AppHeader';
import { RankRow } from '@/components/RankRow';
import { Screen } from './parts/Screen';
import { HighlightsStrip } from './parts/HighlightsStrip';
import { NextBar } from './parts/NextBar';
import { COPY } from './parts/copy';
import { useOwnSnapshot, isSameReveal } from './parts/frozen';
import { FLIP_AFTER_MS, beforeBoard, isHalfway, reorders, sameOrder, scoreBasis } from './parts/moments';
import { boardContextFor, highlightItems, isSettled, type Chase } from './parts/kudosData';
import { useCalm } from './parts/time';

/**
 * The gap under your own row when you are outside the top ten (spec 4.5): to the place directly above when its score
 * is known, otherwise to the top ten. A plain fact; the kudos line beside it reacts and never repeats the number.
 */
export function chaseLine(c: Chase): string {
  const n = c.gap.toLocaleString('en-GB');
  if (c.gap === 0) return `Level on points with ${ordinal(c.place)}. Faster answers break the tie.`;
  if (!c.exact && c.place === 10) return `${n} ${c.gap === 1 ? 'point' : 'points'} off the top ten`;
  return `${n} ${c.gap === 1 ? 'point' : 'points'} behind ${ordinal(c.place)}`;
}

/** M9: the leaderboard, its own screen. Rows move from the old order to the new, your place and line, the round's highlights. */
export default function Leaderboard() {
  const snap = useOwnSnapshot(isSameReveal)!;
  const session = useGame((s) => s.session); const me = useGame((s) => s.me); const boards = useGame((s) => s.boards);
  const revealSeen = useGame((s) => s.revealSeen);
  const reduce = useCalm();
  // 17 Oct 2026: once shown, this board stays (a raised stream delay must not take the phone back to the answer view).
  useEffect(() => { useGame.getState().markBoardShown(snap.index); }, [snap.index]);
  const top = useMemo(() => snap.top ?? [], [snap.top]);
  const prevTop = boards.find((b) => b.index === snap.index - 1)?.top;
  // The scores each row had before this question: the last board this phone saw, or nothing at all after the first question.
  const basis = useMemo(() => scoreBasis(top, snap.index, prevTop), [top, snap.index, prevTop]);
  const before = useMemo(() => beforeBoard(top, basis), [top, basis]);
  const moves = !sameOrder(before, top);
  const reordered = reorders(before, top);

  const [flipped, setFlipped] = useState(() => reduce || !moves);
  const whooshed = useRef(-1);
  useEffect(() => {
    if (flipped) return;
    const t = window.setTimeout(() => {
      setFlipped(true);
      // The whoosh is the sound twin of rows changing places; a board whose order holds only counts its scores up.
      if (reordered && whooshed.current !== snap.index) { whooshed.current = snap.index; sfx.play('whoosh'); }
    }, FLIP_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [flipped, snap.index, reordered]);

  const myId = session?.playerId;
  const rows = flipped ? top : before;
  const prevScore = useMemo(() => new Map((basis ?? []).map((r) => [r.playerId, r.score])), [basis]);
  /** Points each row gained on this question, counted into its score once the rows have moved. */
  const gainOf = (playerId: string, score: number) => {
    const was = prevScore.get(playerId);
    return flipped && was !== undefined && score > was ? score - was : undefined;
  };
  const players = Math.max(snap.playerCount, me?.players ?? 0, top.length);
  const ctx = myId ? boardContextFor({ playerId: myId, index: snap.index, top, me, players }) : undefined;
  const chase = ctx && !ctx.inTop && ctx.rank && ctx.chase ? ctx.chase : undefined;
  // Outside the top ten the gap has its own line, so the kudos line is picked without it (each fact once per screen).
  const line = ctx ? boardLine(chase ? { ...ctx, gapToAbove: undefined, nameAbove: undefined } : ctx) : undefined;
  // Rows on screen carry their own movement and streak; my line carries my own photo finish.
  const onBoard = useMemo(() => [...top.map((r) => r.playerId), ...(myId && ctx?.rank ? [myId] : [])], [top, myId, ctx?.rank]);
  const highlights = highlightItems(snap.highlights, myId, { onBoard, myRank: ctx?.rank });
  const last = snap.index + 1 >= snap.total;
  const halfway = isHalfway(snap.index, snap.total) ? pick(INTROS.halfway, `${snap.code}:${snap.index}:half`) : undefined;
  // My points on this question: the server's own figure once /me has it, else the change since the last board.
  const myRec = me && me.playerId === myId ? me.answers?.[`q${snap.index}`] : undefined;
  const prevMine = myId ? prevScore.get(myId) : undefined;
  const delta = isSettled(myRec) && typeof myRec.points === 'number' ? myRec.points
    : ctx?.score !== undefined && prevMine !== undefined ? ctx.score - prevMine : undefined;
  const nextAt = snap.mode === 'autopilot' && !snap.hold ? snap.revealUntil : undefined;
  const fromAt = boardAt(snap, revealSeen?.index === snap.index ? revealSeen.at : undefined);

  return (
    // The only screen in play without a Pulse of its own, so the wordmark draws its line here.
    <Screen phase="leaderboard" label={COPY.board.title} header={<AppHeader pulse progress={{ index: snap.index, total: snap.total }} />}>
      <header className="flex flex-col gap-1">
        {/* Where the battle stands is the header's progress track, so the title stands alone: no text twice on a screen. */}
        <h1 className="display text-[2.6rem] text-ink">{COPY.board.title}</h1>
        {halfway && <p className="hand text-[1.35rem] leading-snug text-brand-strong">{halfway}</p>}
      </header>

      <HighlightsStrip items={highlights} delay={reduce ? 0 : FLIP_AFTER_MS / 1000 + 0.35} />

      {rows.length > 0 ? (
        <ol className="flex flex-col gap-1.5" aria-label={COPY.board.title} data-flipped={flipped ? 'yes' : 'no'}>
          {rows.map((row) => <RankRow key={row.playerId} row={row} me={row.playerId === myId} showMovement={flipped} delta={gainOf(row.playerId, row.score)} />)}
        </ol>
      ) : (
        <p className="text-ink-2">{COPY.board.notRanked}</p>
      )}

      {ctx && (ctx.rank || line) && (
        <section aria-live="polite" className="flex flex-col gap-2">
          {!ctx.inTop && ctx.rank && (
            <ol aria-label={COPY.board.you} className="flex flex-col">
              <RankRow row={{ playerId: myId!, name: session?.name || me?.name || COPY.board.you, score: ctx.score ?? 0, rank: ctx.rank, prevRank: flipped ? ctx.prevRank : undefined, streak: ctx.streak }} me showMovement={flipped}
                delta={flipped && delta !== undefined && delta > 0 ? delta : undefined} />
            </ol>
          )}
          {chase && <p data-part="chase" className="px-1 text-[15px] font-semibold text-ink-2">{chaseLine(chase)}</p>}
          {/* My points on this question count into my row (+n beside the score), so the line stands alone. */}
          {line && <p className="hand px-1 text-[1.2rem] leading-snug text-ink">{line}</p>}
        </section>
      )}

      <div className="mt-auto pt-2">
        {snap.hold ? (
          <p className="rounded-tile bg-cart-soft px-4 py-3 font-semibold text-ink">{COPY.board.held}</p>
        ) : nextAt !== undefined && fromAt !== undefined ? (
          <NextBar from={fromAt} to={nextAt} label={last ? COPY.board.podium : COPY.board.next} tone={last ? 'cart' : 'brand'} />
        ) : snap.mode === 'autopilot' ? (
          <p className="text-sm font-semibold text-ink-2">{COPY.board.nextSoon}</p>
        ) : (
          <p className="text-sm font-semibold text-ink-2">{COPY.board.manual}</p>
        )}
      </div>
    </Screen>
  );
}
